import React from 'react';
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaAngleDoubleLeft, 
  FaAngleDoubleRight 
} from 'react-icons/fa';
import './AdminPagination.css';

/**
 * Premium Animated Admin Pagination Component
 * 
 * @param {number} totalItems - Total count of records
 * @param {number} currentPage - Currently active 1-indexed page
 * @param {number} pageSize - Number of items per page (default: 10)
 * @param {function} onPageChange - Callback when a page is clicked: (page) => void
 * @param {function} [onPageSizeChange] - Optional callback for changing page size: (size) => void
 * @param {string} [itemLabel='entries'] - Label for items (e.g., 'properties', 'users')
 * @param {Array<number>} [pageSizeOptions=[10, 20, 50, 100]] - Page size dropdown choices
 */
export default function AdminPagination({
  totalItems = 0,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'entries',
  pageSizeOptions = [10, 20, 50, 100]
}) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);

  const startItem = totalItems === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;
  const endItem = Math.min(totalItems, safeCurrentPage * pageSize);

  const handlePageClick = (page) => {
    if (page < 1 || page > totalPages || page === safeCurrentPage) return;
    if (onPageChange) {
      onPageChange(page);
    }
  };

  // Generate page numbers with ellipses
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (safeCurrentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }

    if (safeCurrentPage >= totalPages - 3) {
      return [
        1,
        '...',
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      ];
    }

    return [
      1,
      '...',
      safeCurrentPage - 1,
      safeCurrentPage,
      safeCurrentPage + 1,
      '...',
      totalPages
    ];
  };

  const pages = getPageNumbers();

  return (
    <div className="admin-pagination-container">
      {/* Left side: Results Count & Rows selector */}
      <div className="ap-info-wrap">
        <span className="ap-info-text">
          Showing <span className="ap-highlight">{startItem}</span> to{' '}
          <span className="ap-highlight">{endItem}</span> of{' '}
          <span className="ap-highlight">{totalItems}</span> {itemLabel}
        </span>

        {onPageSizeChange && (
          <div className="ap-size-selector">
            <span>Rows:</span>
            <select
              className="ap-size-select"
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                if (onPageSizeChange) {
                  onPageSizeChange(newSize);
                }
              }}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Right side: Page navigation */}
      <div className="ap-controls-wrap">
        {/* Jump to first page */}
        {totalPages > 4 && (
          <button
            type="button"
            className="ap-btn ap-btn-jump"
            title="First Page"
            disabled={safeCurrentPage === 1}
            onClick={() => handlePageClick(1)}
            aria-label="First page"
          >
            <FaAngleDoubleLeft size={11} />
          </button>
        )}

        {/* Previous page */}
        <button
          type="button"
          className="ap-btn ap-btn-nav ap-btn-prev"
          disabled={safeCurrentPage === 1}
          onClick={() => handlePageClick(safeCurrentPage - 1)}
          aria-label="Previous page"
        >
          <FaChevronLeft size={11} className="ap-nav-icon" />
          <span>Previous</span>
        </button>

        {/* Number pills */}
        {pages.map((p, idx) => {
          if (p === '...') {
            return (
              <span key={`ellipsis-${idx}`} className="ap-ellipsis">
                •••
              </span>
            );
          }

          const isActive = p === safeCurrentPage;
          return (
            <button
              key={p}
              type="button"
              className={`ap-btn ap-btn-num ${isActive ? 'ap-active' : ''}`}
              onClick={() => handlePageClick(p)}
              aria-current={isActive ? 'page' : undefined}
            >
              {p}
            </button>
          );
        })}

        {/* Next page */}
        <button
          type="button"
          className="ap-btn ap-btn-nav ap-btn-next"
          disabled={safeCurrentPage === totalPages || totalItems === 0}
          onClick={() => handlePageClick(safeCurrentPage + 1)}
          aria-label="Next page"
        >
          <span>Next</span>
          <FaChevronRight size={11} className="ap-nav-icon" />
        </button>

        {/* Jump to last page */}
        {totalPages > 4 && (
          <button
            type="button"
            className="ap-btn ap-btn-jump"
            title="Last Page"
            disabled={safeCurrentPage === totalPages || totalItems === 0}
            onClick={() => handlePageClick(totalPages)}
            aria-label="Last page"
          >
            <FaAngleDoubleRight size={11} />
          </button>
        )}
      </div>
    </div>
  );
}
